import React, { useEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import LinearProgress from "@mui/material/LinearProgress";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlus, faImage, faUpload } from "@fortawesome/free-solid-svg-icons";
import Entete from "./dataset/Entete";
import { Button, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import API from "../Authentification/apiAdmin";
import * as XLSX from "xlsx";
import { getProductImage } from "../../Utils/Cloudinary";
// import "../../Styles/AdminDashbord/productManagement.css";

const IMPORT_CHUNK_SIZE = 200;

const EDITABLE_FIELDS = [
  "reference",
  "name",
  "family",
  "price",
  "category",
  "description",
  "disponibility",
  "quantity",
  "selled",
  "sous_category",
  "reste",
];

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    deleted: 0,
    draft: 0,
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ done: 0, total: 0 });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const token = localStorage.getItem("adminToken");

  const handleNavigate = () => {
    navigate("/admin/AddProduct");
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await API.get(
        `/admin/products?page=${page}&search=${search}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setProducts(response.data.data);
      setStats({
        total: response.data.total,
        published: response.data.published,
        deleted: response.data.deleted,
        draft: response.data.draft,
      });
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du chargement des produits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [page, search]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts();
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await API.get("/admin/products/export-all", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const allProducts = response.data.data;

      if (!allProducts || allProducts.length === 0) {
        toast.warning("Aucun produit à exporter");
        return;
      }

      const data = allProducts.map((p) => ({
        id: p.id,
        reference: p.reference,
        name: p.name,
        family: p.family,
        price: p.price,
        category: p.category,
        description: p.description,
        disponibility: p.disponibility,
        quantity: p.quantity,
        selled: p.selled,
        sous_category: p.sous_category,
        reste: p.reste,
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "products");
      XLSX.writeFile(workbook, "products.xlsx");

      toast.success(`Export réussi : ${allProducts.length} produits.`);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'export Excel");
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    if (!importing) fileInputRef.current?.click();
  };

  const buildChunks = (payload, size) => {
    const chunks = [];
    for (let i = 0; i < payload.length; i += size) {
      chunks.push(payload.slice(i, i + size));
    }
    return chunks;
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) return;

    setImporting(true);
    setImportProgress({ done: 0, total: 0 });

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: null });

      if (rows.length === 0) {
        toast.warning("Le fichier est vide.");
        return;
      }

      const payload = rows
        .filter((row) => row.id !== null && row.id !== undefined && String(row.id).trim() !== "")
        .map((row) => {
          const entry = { id: Number(row.id) };
          EDITABLE_FIELDS.forEach((field) => {
            entry[field] = row[field] ?? null;
          });
          return entry;
        })
        .filter((row) => Number.isInteger(row.id));

      if (payload.length === 0) {
        toast.warning("Aucune ligne avec un id valide dans ce fichier.");
        return;
      }

      const chunks = buildChunks(payload, IMPORT_CHUNK_SIZE);
      setImportProgress({ done: 0, total: chunks.length });

      let totalUpdated = 0;
      const allNotFound = [];
      const allErrors = [];
      const failedChunks = [];

      for (let i = 0; i < chunks.length; i++) {
        try {
          const response = await API.post(
            "/admin/products/bulk-update",
            { products: chunks[i] },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          totalUpdated += response.data.updated || 0;
          if (response.data.not_found?.length) allNotFound.push(...response.data.not_found);
          if (response.data.errors?.length) allErrors.push(...response.data.errors);
        } catch (err) {
          console.error(`Échec du lot ${i + 1}/${chunks.length}`, err);
          failedChunks.push(i + 1);
        }

        setImportProgress({ done: i + 1, total: chunks.length });
      }

      toast.success(`${totalUpdated} produit(s) mis à jour sur ${payload.length}.`);

      if (allNotFound.length > 0) {
        toast.warning(
          `${allNotFound.length} id(s) introuvable(s) : ${allNotFound
            .slice(0, 5)
            .join(", ")}${allNotFound.length > 5 ? "…" : ""}`
        );
        console.warn("IDs introuvables :", allNotFound);
      }

      if (allErrors.length > 0) {
        toast.error(`${allErrors.length} ligne(s) en erreur (voir la console).`);
        console.error("Erreurs d'import :", allErrors);
      }

      if (failedChunks.length > 0) {
        toast.error(
          `${failedChunks.length} lot(s) n'ont pas pu être envoyés (problème réseau). Relance simplement l'import : c'est sans risque, les lignes déjà traitées seront juste réappliquées à l'identique.`
        );
      }

      fetchProducts();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la lecture du fichier. Vérifie qu'il s'agit bien d'un .xlsx valide.");
    } finally {
      setImporting(false);
      setImportProgress({ done: 0, total: 0 });
    }
  };

  const importPercent =
    importProgress.total > 0 ? Math.round((importProgress.done / importProgress.total) * 100) : 0;

  return (
    <div className=" product-management-page">
      <Entete title="Gestion des produits" />

      {/* HEADER ACTIONS */}
      <div className="container-fluid">
        <div className="row mt-3 g-2">
          <div className="col-12 col-md-9">
            <div className="row">
              <Button
                className="col-md-3 col-6 me-2 mb-2 bg-success rounded-5"
                style={{ color: "white" }}
                onClick={() => handleNavigate()}
              >
                Ajouter un produit{" "}
                <FontAwesomeIcon icon={faCirclePlus} className="ms-1" />
              </Button>
              <Button
                className="col-md-3 col-6 me-2 mb-2 bg-primary rounded-5"
                style={{ color: "white" }}
                onClick={handleExportExcel}
                disabled={exporting}
              >
                {exporting ? (
                  <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
                ) : null}
                Exporter sous excel
              </Button>
              <Button
                className="col-md-3 col-6 mb-2 rounded-5"
                style={{ color: "white", backgroundColor: "#f68b1e" }}
                onClick={handleImportClick}
                disabled={importing}
              >
                {importing ? (
                  <CircularProgress size={16} sx={{ color: "white", mr: 1 }} />
                ) : (
                  <FontAwesomeIcon icon={faUpload} className="me-1" />
                )}
                Importer Excel
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImportFile}
                accept=".xlsx,.xls"
                style={{ display: "none" }}
              />
            </div>

            {importing && (
              <div className="mt-1 mb-2" style={{ maxWidth: 420 }}>
                <LinearProgress variant="determinate" value={importPercent} />
                <p className="texte_brut" style={{ fontSize: "12px", margin: "4px 0 0" }}>
                  Import en cours… lot {importProgress.done}/{importProgress.total} ({importPercent}%)
                </p>
              </div>
            )}

            <div className="mt-2">
              <p className="texte_brut">
                Produits: Tous ({stats?.total}) |
                <span style={{ color: "blue" }}> Publiés: </span>(
                {stats?.published}) |
                <span style={{ color: "blue" }}> Supprimés: </span>(
                {stats?.deleted}) |
                <span style={{ color: "blue" }}> Brouillons: </span>(
                {stats?.draft})
              </p>
              <p className="texte_brut" style={{ fontSize: "12.5px", color: "#6b7280" }}>
                L'import retrouve chaque produit par sa colonne <strong>id</strong> (ne pas la
                modifier ni la supprimer dans le fichier). Toutes les autres colonnes, y compris{" "}
                <strong>reference</strong>, sont modifiables.
              </p>
            </div>
          </div>

          {/* BARRE DE RECHERCHE */}
          <div className="col-12 col-md">
            <form onSubmit={handleSearch}>
              <div className="row overflow-hidden border border-1">
                <div className="col-10">
                  <InputBase
                    placeholder="Rechercher un produit"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    inputProps={{ "aria-label": "search" }}
                    className="w-100 px-3 h-100"
                  />
                </div>
                <div className="col-2" style={{ backgroundColor: "#0066BD" }}>
                  <IconButton
                    type="submit"
                    className="w-100 h-100"
                    sx={{
                      color: "white",
                      borderRadius: 0,
                      ":hover": { backgroundColor: "#0066BD" },
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* TABLEAU PRODUITS */}
      <div className=" mt-3">
        {loading ? (
          <div className="text-center my-5">
            <CircularProgress />
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr className="petit_titre row">
                  <th className="col-md-1 col-2">
                    <FontAwesomeIcon icon={faImage} />
                  </th>
                  <th className="col-1 d-none d-md-block">Référence</th>
                  <th className="col-md-2 col-3">Nom</th>
                  <th className="col-1 d-none d-md-block">Famille</th>
                  <th className="col-md-1 col-3">Prix</th>
                  <th className="col-md-1 col-3">Catégorie</th>
                  <th className="col-2 d-none d-md-block">Description</th>
                  <th className="col-1">Disponibilité</th>
                  <th className="col-2 d-none d-md-block">Sous-catégorie</th>
                </tr>
              </thead>
              <tbody>
                {products.length > 0 ? (
                  products.map((p) => (
                    <tr key={p.id} className="row">
                      <td className="col-md-1 col-2">
                        <img src={getProductImage(p.image)} alt={p.name} width="50" />
                      </td>
                      <td className="texte_brut col-1 d-none d-md-block">{p.reference}</td>
                      <td className="texte_brut col-md-2 col-3">{p.name}</td>
                      <td className="texte_brut col-1 d-none d-md-block">{p.family}</td>
                      <td className="texte_brut col-md-1 col-3">{p.price} FCFA</td>
                      <td className="texte_brut col-md-1 col-3">{p.category}</td>
                      <td className="texte_brut col-2 d-none d-md-block">{p.description}</td>
                      <td className="texte_brut col-1">{p.disponibility}</td>
                      <td className="texte_brut col-2 d-none d-md-block">{p.sous_category}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center">
                      Aucun produit trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* PAGINATION */}
      {totalPages > 1 && (
        <nav
          aria-label="Pagination"
          className="d-flex justify-content-center mt-3"
        >
          <ul className="pagination mb-0">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage(page - 1)}>
                Précédent
              </button>
            </li>
            <li className="page-item disabled">
              <span className="page-link">
                {page} / {totalPages}
              </span>
            </li>
            <li
              className={`page-item ${page === totalPages ? "disabled" : ""}`}
            >
              <button className="page-link" onClick={() => setPage(page + 1)}>
                Suivant
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

export default ProductManagement;